<script setup>
// §8.2 Drawer — elevated. Used for entry edit + split (§8.2).
// Also the NATIVE <dialog> element, positioned as a right-hand sheet. Same
// free focus-trap / Esc / backdrop as the modal — the only difference is layout
// and a slide-in transition.
import { computed, ref, watch, onMounted } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: null },
  width: { type: [String, Number], default: null },
})
const open = defineModel('open', { default: false })
const dlg = ref(null)
const drawerStyle = computed(() => {
  if (props.width === null || props.width === undefined || props.width === '') return null
  return { width: typeof props.width === 'number' ? `${props.width}px` : props.width }
})

function sync(v) {
  const el = dlg.value
  if (!el) return
  if (v && !el.open) el.showModal()
  else if (!v && el.open) el.close()
}
watch(open, sync)
onMounted(() => sync(open.value))

function onClose() {
  open.value = false
}
function onClick(e) {
  if (e.target === dlg.value) open.value = false
}
</script>

<template>
  <dialog ref="dlg" class="l-drawer" :style="drawerStyle" @close="onClose" @click="onClick">
    <div class="inner">
      <div class="dh">
        <div>
          <h2 class="dt">{{ title }}</h2>
          <p v-if="description" class="dd">{{ description }}</p>
        </div>
        <button class="dx" type="button" aria-label="Close" @click="open = false">
          <LIcon name="xmark" :size="16" />
        </button>
      </div>
      <div class="db"><slot /></div>
      <div v-if="$slots.footer" class="df"><slot name="footer" /></div>
    </div>
  </dialog>
</template>

<style scoped>
.l-drawer {
  /* pin to the right edge, full height; override the dialog's default centring */
  margin: 0 0 0 auto;
  height: 100%;
  max-height: 100vh;
  width: 440px;
  max-width: 100vw;
  padding: 0;
  border: 0;
  background: var(--surface);
  color: var(--text);
  box-shadow: var(--shadow);
}
.l-drawer::backdrop {
  background: var(--overlay);
}
.l-drawer[open] {
  animation: drawer-in var(--t-fast) var(--ease);
}
@keyframes drawer-in {
  from {
    transform: translateX(16px);
    opacity: 0;
  }
}
.inner {
  height: 100%;
  padding: var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  overflow-y: auto;
}
.dh {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--sp-3);
}
.dt {
  font-family: var(--font-display);
  font-size: var(--text-card);
  font-weight: 600;
}
.dd {
  font-size: var(--text-sm);
  color: var(--text-3);
  margin-top: 3px;
}
.dx {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--target-min);
  height: var(--target-min);
  border-radius: var(--r-sm);
  color: var(--text-2);
  flex: none;
}
.dx:hover {
  background: var(--surface-sunken);
  color: var(--text);
}
.db {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}
.df {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  border-top: 1px solid var(--line-soft);
  padding-top: var(--sp-4);
}
</style>
